/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import PropTypes from "prop-types";
import React, { createContext, useEffect, useMemo, useState } from "react";
import { useCloud } from "./Cloud";

// Create a new React context for managing region state
export const RegionContext = createContext();

// Define a provider component for the RegionContext
export const RegionContextProvider = ({ children }) => {
  const { cloud } = useCloud();
  const [ regions, setRegions ] = useState(window.ENV[cloud].regions || []);
  const [ region, setRegion ] = useState(regions[0]);

  useEffect(() => {
    setRegions(window.ENV[cloud].regions || []);
    setRegion(regions[0]);
  }, [ cloud, regions ]);

  // Create a memoized value object to prevent unnecessary re-renders
  const value = useMemo(() => ({ region, setRegion }), [ region, setRegion ]);

  return <RegionContext.Provider value={ value }>{ children }</RegionContext.Provider>;
};

RegionContextProvider.propTypes = {
  children: PropTypes.any
};

export default RegionContext;

// Custom hook to conveniently access the region context values
export const useRegion = () => {
  const context = React.useContext(RegionContext);

  // Throw an error if the hook is used outside the RegionContextProvider
  if (context === undefined) {
    throw new Error("useRegion must be used within a RegionContextProvider");
  }

  // Destructure the context values for easy access
  const { region, setRegion } = context;

  return { region, setRegion };
};
